import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AppLink as Link } from "@/components/app-link";
import { useTranslation } from "react-i18next";
import { Collapsible as CollapsiblePrimitive } from "@base-ui/react/collapsible";
import {
  Building2,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Flag,
  Image as ImageIcon,
  LayoutDashboard,
  Megaphone,
  ScrollText,
  Tags,
  Users,
} from "lucide-react";

import { Wordmark } from "@/components/brand/wordmark";
import { DirectionalIcon } from "@/components/ui/directional-icon";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { currentUserIsAdmin } from "@/lib/admin";
import { cn } from "@/lib/utils";

type NavItem = {
  href: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: { href: string; labelKey: string }[];
};

type NavGroup = {
  labelKey: string;
  items: NavItem[];
};

const STAFF_NAV_GROUPS: NavGroup[] = [
  {
    labelKey: "nav.groups.overview",
    items: [{ href: "/", labelKey: "nav.dashboard", icon: LayoutDashboard }],
  },
  {
    labelKey: "nav.groups.moderation",
    items: [
      { href: "/users", labelKey: "nav.users", icon: Users },
      { href: "/reports", labelKey: "nav.reports", icon: Flag },
      {
        href: "/content/posts",
        labelKey: "nav.content",
        icon: FileText,
        children: [
          { href: "/content/posts", labelKey: "nav.posts" },
          { href: "/content/comments", labelKey: "nav.comments" },
          { href: "/content/messages", labelKey: "nav.messages" },
        ],
      },
      { href: "/media", labelKey: "nav.media", icon: ImageIcon },
    ],
  },
];

const ADMIN_NAV_GROUPS: NavGroup[] = [
  ...STAFF_NAV_GROUPS,
  {
    labelKey: "nav.groups.management",
    items: [
      { href: "/communities", labelKey: "nav.communities", icon: Building2 },
      {
        href: "/taxonomy/skills",
        labelKey: "nav.taxonomy",
        icon: Tags,
        children: [
          { href: "/taxonomy/skills", labelKey: "nav.skills" },
          { href: "/taxonomy/platforms", labelKey: "nav.platforms" },
        ],
      },
      { href: "/notifications/broadcast", labelKey: "nav.notifications", icon: Megaphone, children: [{ href: "/notifications/broadcast", labelKey: "nav.broadcast" }] },
      { href: "/audit", labelKey: "nav.audit", icon: ScrollText },
    ],
  },
  {
    // Hidden from moderators by living here rather than in STAFF_NAV_GROUPS.
    // The gate that counts is `TalentStaffGuard` on talent-api: hiding a nav
    // item is a courtesy, not an authorization boundary.
    labelKey: "nav.groups.talent",
    items: [
      {
        href: "/talent/reviews",
        labelKey: "nav.talentReviews",
        icon: ClipboardCheck,
      },
    ],
  },
];

function isActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

function NavGroupItem({
  item,
  pathname,
}: {
  item: NavItem & { children: NonNullable<NavItem["children"]> };
  pathname: string;
}) {
  const { t } = useTranslation("admin");
  const Icon = item.icon;
  const groupActive =
    isActive(pathname, item.href) ||
    item.children.some((child) => isActive(pathname, child.href));
  const [open, setOpen] = useState(groupActive);

  useEffect(() => {
    if (groupActive) setOpen(true);
  }, [groupActive]);

  return (
    <CollapsiblePrimitive.Root
      open={open}
      onOpenChange={setOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <SidebarMenuButton
          render={<Link href={item.href} />}
          isActive={groupActive}
          tooltip={t(item.labelKey)}
        >
          <Icon />
          <span>{t(item.labelKey)}</span>
        </SidebarMenuButton>
        <CollapsiblePrimitive.Trigger
          render={
            <SidebarMenuAction
              aria-label={t("shell.toggleNavGroup", { label: t(item.labelKey) })}
            />
          }
        >
          <DirectionalIcon
            icon={ChevronRight}
            className={cn(
              "transition-transform duration-200",
              "group-data-[state=open]/collapsible:rotate-90",
              "rtl:group-data-[state=open]/collapsible:-rotate-90",
            )}
          />
        </CollapsiblePrimitive.Trigger>
        <CollapsiblePrimitive.Panel>
          <SidebarMenuSub>
            {item.children.map((child) => (
              <SidebarMenuSubItem key={child.href}>
                <SidebarMenuSubButton
                  render={<Link href={child.href} />}
                  isActive={isActive(pathname, child.href)}
                >
                  <span>{t(child.labelKey)}</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsiblePrimitive.Panel>
      </SidebarMenuItem>
    </CollapsiblePrimitive.Root>
  );
}

export function AdminSidebar() {
  const { pathname } = useLocation();
  const { t } = useTranslation("admin");
  const navGroups = currentUserIsAdmin() ? ADMIN_NAV_GROUPS : STAFF_NAV_GROUPS;

  return (
    <Sidebar collapsible="icon" variant="inset" aria-label={t("shell.sidebarNav")}>
      <SidebarHeader className="border-b border-sidebar-border px-4 py-3 group-data-[collapsible=icon]:px-2">
        <Link
          href="/"
          aria-label={t("a11y.logoAlt")}
          className="flex min-w-0 flex-col gap-1 rounded-md outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          <Wordmark
            alt=""
            className="h-7 max-w-full group-data-[collapsible=icon]:h-6 group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:object-cover group-data-[collapsible=icon]:object-left"
          />
          <span className="truncate text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
            {t("brand.subtitle")}
          </span>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.labelKey}>
            <SidebarGroupLabel>{t(group.labelKey)}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(pathname, item.href);

                  if (item.children?.length) {
                    return (
                      <NavGroupItem
                        key={item.href}
                        item={{ ...item, children: item.children }}
                        pathname={pathname}
                      />
                    );
                  }

                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton
                        render={<Link href={item.href} />}
                        isActive={active}
                        tooltip={t(item.labelKey)}
                      >
                        <Icon />
                        <span>{t(item.labelKey)}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}
